import { z } from "zod";

/**
 * A saved Clicks view: a named snapshot of how the table is set up. It captures
 * everything that lives in the URL (grouping, filters, date range) plus the
 * visible columns (which live in component state, not the URL). Sort is left out
 * of v1 — it lives inside the grid, not here.
 *
 * Stored per-browser in localStorage. The pure functions below (add/remove/
 * parse/serialize) have no browser dependency so they unit-test on their own;
 * load/persist are the thin, try/catch'd localStorage edges.
 */
export interface SavedView {
  id: string;
  name: string;
  groupDims: string[];
  visibleColumns: string[];
  /** The raw `filters` URL param (JSON string), or "" for none. */
  filters: string;
  /**
   * The raw `where` URL param - the column conditions ("Revenue > 1000"), in
   * the URL since 30.10.2026. Absent in views saved before: they had none.
   */
  where?: string;
  dateRange: string;
  customFrom?: string;
  customTo?: string;
}

export const SAVED_VIEWS_KEY = "oracul.clicks.savedViews.v1";

const savedViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  groupDims: z.array(z.string()),
  visibleColumns: z.array(z.string()),
  filters: z.string(),
  where: z.string().optional(),
  dateRange: z.string(),
  customFrom: z.string().optional(),
  customTo: z.string().optional(),
});

const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Upsert by name: saving a view whose name already exists overwrites it in
 * place (so "save" twice doesn't pile up duplicates), otherwise it's appended.
 */
export const addView = (list: SavedView[], view: SavedView): SavedView[] => {
  const index = list.findIndex((v) => sameName(v.name, view.name));
  if (index === -1) return [...list, view];
  const next = [...list];
  next[index] = view;
  return next;
};

export const removeView = (list: SavedView[], id: string): SavedView[] =>
  list.filter((v) => v.id !== id);

/** Safe parse of the stored JSON; bad entries are dropped, junk input → []. */
export const parseViews = (raw: string | null): SavedView[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: SavedView[] = [];
  for (const item of parsed) {
    const result = savedViewSchema.safeParse(item);
    if (result.success) out.push(result.data);
  }
  return out;
};

export const serializeViews = (list: SavedView[]): string =>
  JSON.stringify(list);

export const loadViews = (): SavedView[] => {
  try {
    return parseViews(localStorage.getItem(SAVED_VIEWS_KEY));
  } catch {
    return [];
  }
};

export const persistViews = (list: SavedView[]): SavedView[] => {
  try {
    localStorage.setItem(SAVED_VIEWS_KEY, serializeViews(list));
  } catch {
    // Private mode / disabled storage — the list still works for this session.
  }
  return list;
};
